import { LoadingOverlay, Title } from '@mantine/core';
import Breadcrumbs, { BreadcrumbItem } from 'components/BreadcrumbsComponent';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schoolService } from 'services/dacum/school.service';
import { showSuccessNotification, showErrorNotification } from 'utils/notification';
import { SchoolJobFamilyForm, UpdateSchoolJobFamilyForm } from 'types/dacum/schoolManagement';
import CreateJobFamilyForm from '../../../../../../components/forms/dacum/CreateJobFamilyForm';
import { useSchoolJobFamilyStore } from 'store/schoolJobFamily';

const CreateJobFamilyPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { formData, mode, setFormData, setMode } = useSchoolJobFamilyStore();

  const { data: jobFamilyDetail, isLoading } = useQuery({
    queryKey: ['jobFamilyDetail', id],
    queryFn: () => schoolService.getSchoolJobFamilyDetail(id as string),
    enabled: !!id
  });

  useEffect(() => {
    if (id && jobFamilyDetail && jobFamilyDetail.data) {
      setMode('edit');
      setFormData({
        msSchoolCompetencyManagementID: jobFamilyDetail.data.msSchoolCompetencyManagementID,
        jobFamilyID: jobFamilyDetail.data.jobFamilyID,
        trSchoolJobFamilyID: jobFamilyDetail.data.trSchoolJobFamilyID
      });
    }
  }, [id, jobFamilyDetail, setMode, setFormData]);

  const breadcrumbs = [
    { title: 'School Management', href: pathnames.schoolManagement },
    { title: 'Job Family', href: pathnames.jobFamily },
    { title: mode === 'add' ? 'New' : 'Edit', href: '' }
  ];

  const createJobFamily = useMutation({
    mutationFn: (data: SchoolJobFamilyForm) => schoolService.createSchoolJobFamily(data),
    onSuccess: () => {
      showSuccessNotification({
        title: 'Job Family Created',
        message: 'Job Family has been created successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['schoolJobFamily'] });
      navigate(pathnames.jobFamily);
    },
    onError: () => {
      showErrorNotification({
        title: 'Failed to create job family',
        message: 'Failed to create job family, please try again later.'
      });
    }
  });

  const updateJobFamily = useMutation({
    mutationFn: (data: UpdateSchoolJobFamilyForm) => {
      return schoolService.updateSchoolJobFamily({
        ...data,
        trSchoolJobFamilyID: formData.trSchoolJobFamilyID as string
      });
    },
    onSuccess: () => {
      showSuccessNotification({
        title: 'Job Family Updated',
        message: 'Job Family has been updated successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['schoolJobFamily'] });
      navigate(pathnames.jobFamily);
    },
    onError: () => {
      showErrorNotification({
        title: 'Failed to update job family',
        message: 'Failed to update job family, please try again later.'
      });
    }
  });

  return (
    <>
      <Breadcrumbs items={breadcrumbs as BreadcrumbItem[]} mb={32} />
      <Title order={2} mb={30}>
        {mode === 'add' ? 'Create New' : 'Edit'} Job Family
      </Title>
      <CreateJobFamilyForm createJobFamily={createJobFamily} updateJobFamily={updateJobFamily} />
      <LoadingOverlay visible={isLoading} />
    </>
  );
};

export default CreateJobFamilyPage;
